import { withTranslation } from "next-i18next";
import useCustomizableCloset from "@hooks/use-customizable-closet";
import {
  useBalReturnAvailableFor,
  useLocalCarrierName,
  usePickupCarriersFor,
} from "@hooks/use-addresses";
import { Button, Image, Link } from "@components/core";
import carriers from "@utils/constants/carriers";
import { getReturnLink } from "@utils/getReturnLink";

function ReturnMyClosetGuide({ startAnimation, t }) {
  const currentCloset = useCustomizableCloset();
  const balReturnAvailableForCountry = useBalReturnAvailableFor(currentCloset.return_country);
  const pickupCarriersForCountry = usePickupCarriersFor(currentCloset.return_country);

  const carrierName = currentCloset.return_carrier_name;
  const localCarrierName = useLocalCarrierName(carrierName, currentCloset.return_country);
  const balReturnAvailable = carrierName === carriers.colissimo && balReturnAvailableForCountry;

  const mailboxDropoffStep = () => {
    const linkUrl = getReturnLink(localCarrierName, currentCloset.return_country);
    return (
      <div className="font-weight-bold">
        <p className="mb-1">
          {t(
            "common:drawer.modal_steps_to_return_closet.drop_your_closet_off_in_your_mailbox.title",
          )}
        </p>
        <p className="font-weight-normal mb-0 text-gray">
          {t(
            "common:drawer.modal_steps_to_return_closet.drop_your_closet_off_in_your_mailbox.text",
          )}
        </p>
        <Link href={linkUrl}>
          <Button
            variant="link-primary"
            className="no-underline"
            trackingName="drawerReceived_returnGuide_FindRelayPoint"
          >
            {t("common:drawer.modal_steps_to_return_closet.find_a_relay_point")}
          </Button>
        </Link>
      </div>
    );
  };

  const relayPointDropoffStep = () => {
    return (
      <div className="font-weight-bold">
        <p className="mb-1">
          {t(
            "common:drawer.modal_steps_to_return_closet.drop_your_closet_off_at_a_relay_point.title",
          )}
        </p>
        <p className="font-weight-normal mb-0 text-gray">
          {t(
            "common:drawer.modal_steps_to_return_closet.drop_your_closet_off_at_a_relay_point.text",
            {
              carrier: t(
                `common:drawer.modal_steps_to_return_closet.drop_your_closet_off_at_a_relay_point.carrier.${localCarrierName}`,
              ),
            },
          )}
        </p>
      </div>
    );
  };

  const goToBalRequestPage = () => {
    return (
      <Link href={`/carriers/closet_return?closet_id=${currentCloset?.id}`}>
        <Button
          fullWidth
          trackingName="drawerReceived_returnGuide_BALRequest"
        >
          {t("common:drawer.modal_steps_to_return_closet.request_mailbox_collection")}
        </Button>
      </Link>
    );
  };

  const goToRelayPointPage = () => {
    const linkUrl = getReturnLink(localCarrierName, currentCloset.return_country);

    return (
      <a
        href={linkUrl}
        target={!pickupCarriersForCountry.includes(carrierName) ? "_blank" : "_self"}
        rel="noopener noreferrer"
      >
        <Button
          fullWidth
          trackingName="drawerReceived_returnGuide_FindRelayPoint"
        >
          {t("common:drawer.modal_steps_to_return_closet.find_a_relay_point")}
        </Button>
      </a>
    );
  };

  return [
    <div
      className="drawer-body bg-white p-4"
      key="drawer-steps-to-return-closet-body"
    >
      <div className="p-2 text-center">
        <h5 className="mb-2">
          {t("common:drawer.modal_steps_to_return_closet.how_to_return_my_closet")}
        </h5>
        <p className="mb-3 text-gray">
          {t(
            "common:drawer.modal_steps_to_return_closet.return_your_closet_to_choose_your_next_selection",
          )}
        </p>
      </div>

      <div
        style={{ width: "154px" }}
        className="mx-auto"
      >
        <Image
          src="deliveryLightNeon"
          alt="delivery-illustration"
          className="w-100"
        />
      </div>

      <div className="py-4 m-2">
        <div className="d-flex flex-column gap-4 mb-0 px-2">
          <div className="font-weight-bold">
            <p className="mb-1">
              {t("common:drawer.modal_steps_to_return_closet.prepare_your_parcel")}
            </p>
            <p className="font-weight-normal mb-0 text-gray">
              {t(
                "common:drawer.modal_steps_to_return_closet.place_unpurchased_items_in_the_reusable_pouch",
              )}
            </p>
          </div>

          {balReturnAvailable ? mailboxDropoffStep() : relayPointDropoffStep()}

          <div className="font-weight-bold">
            <p className="mb-1">
              {t("common:drawer.modal_steps_to_return_closet.unlock_your_next_closet")}
            </p>
            <p className="font-weight-normal mb-0 text-gray">
              {t(
                "common:drawer.modal_steps_to_return_closet.we_will_let_you_know_as_soon_as_the_carrier_has_scanned_your_return",
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="d-flex flex-column justify-content-center py-3">
        <p className="mb-0 text-center text-gray">
          {t("common:drawer.modal_steps_to_return_closet.have_you_already_returned_your_closet")}
        </p>
        <Button
          variant="link-primary"
          className="mt-1 no-underline"
          onClick={startAnimation}
          trackingName="drawerReceived_returnGuide_CheckMyReturn"
        >
          {t("common:drawer.modal_steps_to_return_closet.check_my_parcel")}
        </Button>
      </div>

      <div className="d-flex flex-column justify-content-center pt-3">
        <p className="mb-0 text-center text-gray">
          {t("common:drawer.modal_steps_to_return_closet.lost_your_label")}
        </p>
        <Link href={currentCloset?.return_slip_url}>
          <Button
            variant="link-primary"
            fullWidth
            className="no-underline"
            trackingName="drawerReceived_returnGuide_PrintLabel"
          >
            {t("common:drawer.modal_steps_to_return_closet.reprint_my_label")}
          </Button>
        </Link>
      </div>
    </div>,

    <div
      className="p-3 border-top bg-white"
      key="drawer-steps-to-return-closet-footer"
    >
      {balReturnAvailable ? goToBalRequestPage() : goToRelayPointPage()}
    </div>,
  ];
}

export default withTranslation("common")(ReturnMyClosetGuide);
